import React, { useState, useEffect } from 'react';
import { X, Eye, Trash2, Copy, Link2, Check, Download, Database, QrCode, Search, ChevronDown, ChevronRight, Shield, SlidersHorizontal, RotateCcw } from 'lucide-react';
import clsx from 'clsx';
import toast from 'react-hot-toast';
import { getUsers, updateUser, updateUserPositions, deleteUser, createInviteLink, bindFeishuForUser, unbindFeishuForUser, resetUserPassword, getBackupStats, downloadBackup, getPermissionModules, getUserPermissions, updateUserPermissions, resetUserPermissions } from '../api';
import { User, Position } from '../types';
import PermissionManager from './PermissionManager';

interface Props {
  currentUser: User;
  positions: Position[];
  onClose?: () => void;
  embedded?: boolean;
}

const ROLE_LABELS: Record<string, string> = { admin: '管理员', hr: 'HR', vip: 'VIP面试官', interviewer: '面试官', referrer: '内推官', it: 'IT资源官' };
const ROLE_COLORS: Record<string, string> = {
  admin: 'bg-red-50 text-red-600',
  hr: 'bg-blue-50 text-blue-600',
  vip: 'bg-amber-50 text-amber-600',
  interviewer: 'bg-gray-50 text-gray-600',
  referrer: 'bg-purple-50 text-purple-600',
  it: 'bg-cyan-50 text-cyan-600',
};
const ROLE_GROUP_ORDER = ['admin', 'hr', 'vip', 'interviewer', 'it', 'referrer'];

export default function TeamManager({ currentUser, positions, onClose, embedded = false }: Props) {
  const [users, setUsers] = useState<User[]>([]);
  const [showInvite, setShowInvite] = useState(false);
  const [invRole, setInvRole] = useState('interviewer');
  const [invPositionIds, setInvPositionIds] = useState<string[]>([]);
  const [generatedLink, setGeneratedLink] = useState('');
  const [linkCopied, setLinkCopied] = useState(false);
  const [editingPositions, setEditingPositions] = useState<string | null>(null);
  const [bindingFeishu, setBindingFeishu] = useState<string | null>(null);
  const [feishuContact, setFeishuContact] = useState('');
  const [resettingPassword, setResettingPassword] = useState<string | null>(null);
  const [newPassword, setNewPassword] = useState('');
  const [loading, setLoading] = useState(true);
  const [backupStats, setBackupStats] = useState<any>(null);
  const [loadingBackup, setLoadingBackup] = useState(false);
  const [downloadingBackup, setDownloadingBackup] = useState(false);
  // Search + grouping + permission management
  const [view, setView] = useState<'members' | 'roles'>('members');
  const [search, setSearch] = useState('');
  const [collapsedRoles, setCollapsedRoles] = useState<Record<string, boolean>>({});
  const [moduleDefs, setModuleDefs] = useState<{ key: string; label: string }[]>([]);
  const [editingPerms, setEditingPerms] = useState<string | null>(null);
  const [permDraft, setPermDraft] = useState<{ modules: string[]; position_scope: 'all' | 'specific'; usesOverride: boolean } | null>(null);
  const [savingPerms, setSavingPerms] = useState(false);

  const isAdmin = currentUser.role === 'admin';

  const fetchUsers = async () => {
    try {
      const data = await getUsers();
      setUsers(data);
    } catch { toast.error('获取用户列表失败'); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchUsers(); }, []);
  useEffect(() => {
    getPermissionModules().then(d => setModuleDefs(d.modules || [])).catch(() => {});
  }, []);

  const openPermsEditor = async (userId: string) => {
    if (editingPerms === userId) { setEditingPerms(null); setPermDraft(null); return; }
    try {
      const data = await getUserPermissions(userId);
      const usesOverride = data.override?.modules != null || data.override?.position_scope != null;
      setPermDraft({
        modules: (data.override?.modules ?? data.effective?.modules) || [],
        position_scope: (data.override?.position_scope ?? data.effective?.position_scope) || 'specific',
        usesOverride,
      });
      setEditingPerms(userId);
    } catch { toast.error('获取权限失败'); }
  };

  const togglePermModule = (key: string) => {
    setPermDraft(prev => prev ? { ...prev, modules: prev.modules.includes(key) ? prev.modules.filter(m => m !== key) : [...prev.modules, key] } : prev);
  };

  const handleSavePerms = async (userId: string) => {
    if (!permDraft) return;
    setSavingPerms(true);
    try {
      await updateUserPermissions(userId, { modules: permDraft.modules, position_scope: permDraft.position_scope });
      toast.success('个人权限已更新');
      setEditingPerms(null);
      setPermDraft(null);
    } catch (err: any) {
      toast.error(err.response?.data?.message || '保存失败');
    } finally { setSavingPerms(false); }
  };

  const handleResetPerms = async (userId: string) => {
    setSavingPerms(true);
    try {
      await resetUserPermissions(userId);
      toast.success('已恢复为角色默认权限');
      setEditingPerms(null);
      setPermDraft(null);
    } catch (err: any) {
      toast.error(err.response?.data?.message || '重置失败');
    } finally { setSavingPerms(false); }
  };

  const toggleRoleGroup = (role: string) => {
    setCollapsedRoles(prev => ({ ...prev, [role]: !prev[role] }));
  };

  const fetchBackupStats = async () => {
    setLoadingBackup(true);
    try { setBackupStats(await getBackupStats()); }
    catch { toast.error('获取备份信息失败'); }
    finally { setLoadingBackup(false); }
  };

  const handleDownloadBackup = async () => {
    setDownloadingBackup(true);
    try {
      const blob = await downloadBackup();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `ats_backup_${new Date().toISOString().slice(0, 10)}.zip`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('备份已下载');
    } catch { toast.error('备份下载失败'); }
    finally { setDownloadingBackup(false); }
  };

  const handleGenerateLink = async () => {
    if (invRole === 'referrer') {
      setGeneratedLink(`${window.location.origin}/referral-poster`);
      setLinkCopied(false);
      return;
    }
    try {
      const data = await createInviteLink({ role: invRole, position_ids: invPositionIds });
      const baseUrl = window.location.origin;
      const link = `${baseUrl}?invite=${data.code}`;
      setGeneratedLink(link);
      setLinkCopied(false);
    } catch (err: any) {
      toast.error(err.response?.data?.message || '生成邀请链接失败');
    }
  };

  const handleCopyLink = () => {
    navigator.clipboard.writeText(generatedLink).then(() => {
      setLinkCopied(true);
      toast.success('已复制邀请链接');
      setTimeout(() => setLinkCopied(false), 2000);
    });
  };

  const handleOpenQrCode = () => {
    if (!generatedLink) return;
    const qrUrl = `https://api.qrserver.com/v1/create-qr-code/?size=600x600&data=${encodeURIComponent(generatedLink)}`;
    window.open(qrUrl, '_blank');
  };

  const handleResetInvite = () => {
    setShowInvite(false);
    setGeneratedLink('');
    setInvRole('interviewer');
    setInvPositionIds([]);
    setLinkCopied(false);
  };

  const handleBindFeishu = async (userId: string) => {
    const val = feishuContact.trim();
    if (!val) { toast.error('请输入手机号或邮箱'); return; }
    const isEmail = val.includes('@');
    try {
      await bindFeishuForUser(userId, isEmail ? { email: val } : { mobile: val });
      toast.success('飞书账号绑定成功');
      setBindingFeishu(null);
      setFeishuContact('');
      fetchUsers();
    } catch (err: any) {
      toast.error(err.response?.data?.message || '绑定失败');
    }
  };

  const handleUnbindFeishu = async (userId: string) => {
    try {
      await unbindFeishuForUser(userId);
      toast.success('已解绑飞书账号');
      setBindingFeishu(null);
      setFeishuContact('');
      fetchUsers();
    } catch (err: any) {
      toast.error(err.response?.data?.message || '解绑失败');
    }
  };

  const handleRoleChange = async (userId: string, role: string) => {
    try {
      await updateUser(userId, { role });
      toast.success('角色已更新');
      fetchUsers();
    } catch (err: any) {
      toast.error(err.response?.data?.message || '更新失败');
    }
  };

  const handleStatusToggle = async (userId: string, currentStatus: string) => {
    try {
      await updateUser(userId, { status: currentStatus === 'active' ? 'disabled' : 'active' });
      toast.success('状态已更新');
      fetchUsers();
    } catch (err: any) {
      toast.error(err.response?.data?.message || '更新失败');
    }
  };

  const handlePositionToggle = (posId: string, targetIds: string[], setter: (ids: string[]) => void) => {
    setter(targetIds.includes(posId) ? targetIds.filter(id => id !== posId) : [...targetIds, posId]);
  };

  const handleSavePositions = async (userId: string, posIds: string[]) => {
    try {
      await updateUserPositions(userId, posIds);
      toast.success('岗位权限已更新');
      setEditingPositions(null);
      fetchUsers();
    } catch (err: any) {
      toast.error(err.response?.data?.message || '更新失败');
    }
  };

  const handleResetPassword = async (userId: string) => {
    if (!newPassword || newPassword.length < 4) { toast.error('密码至少4位'); return; }
    try {
      await resetUserPassword(userId, newPassword);
      toast.success('密码已重置');
      setResettingPassword(null);
      setNewPassword('');
    } catch (err: any) {
      toast.error(err.response?.data?.message || '重置失败');
    }
  };

  const handleDelete = async (userId: string, name: string) => {
    if (!window.confirm(`确认删除「${name}」？`)) return;
    try {
      await deleteUser(userId);
      toast.success('已删除');
      fetchUsers();
    } catch (err: any) {
      toast.error(err.response?.data?.message || '删除失败');
    }
  };

  const q = search.trim().toLowerCase();
  const filteredUsers = q
    ? users.filter(u =>
        (u.display_name || '').toLowerCase().includes(q) ||
        (u.username || '').toLowerCase().includes(q) ||
        (u.phone || '').toLowerCase().includes(q) ||
        (ROLE_LABELS[u.role] || '').toLowerCase().includes(q))
    : users;
  const groupedUsers: Record<string, User[]> = {};
  for (const u of filteredUsers) {
    (groupedUsers[u.role] = groupedUsers[u.role] || []).push(u);
  }
  const visibleRoles = ROLE_GROUP_ORDER.filter(r => (groupedUsers[r] || []).length > 0);

  const content = (
      <div className={embedded ? "h-full bg-white overflow-hidden flex flex-col" : "relative w-[600px] max-h-[80vh] bg-white rounded-2xl shadow-2xl overflow-hidden flex flex-col animate-modal-in"}>
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-bold text-gray-900">团队管理</h2>
          {!embedded && (
            <button onClick={onClose} className="p-1.5 rounded-full hover:bg-gray-100 text-gray-400 hover:text-gray-600">
              <X size={18} />
            </button>
          )}
        </div>

        {/* Tab switcher */}
        <div className="flex items-center gap-1 px-6 pt-3 border-b border-gray-100">
          <button
            onClick={() => setView('members')}
            className={clsx('flex items-center gap-1.5 px-3 pb-2.5 text-sm font-medium border-b-2 -mb-px transition-colors',
              view === 'members' ? 'border-primary-500 text-primary-600' : 'border-transparent text-gray-400 hover:text-gray-600')}
          >
            <SlidersHorizontal size={14} />成员管理
          </button>
          <button
            onClick={() => setView('roles')}
            className={clsx('flex items-center gap-1.5 px-3 pb-2.5 text-sm font-medium border-b-2 -mb-px transition-colors',
              view === 'roles' ? 'border-primary-500 text-primary-600' : 'border-transparent text-gray-400 hover:text-gray-600')}
          >
            <Shield size={14} />角色权限
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-6 space-y-4">
          {view === 'roles' && <PermissionManager />}

          {view === 'members' && (<>
          {/* Search engine */}
          <div className="relative">
            <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="搜索成员（姓名 / 账号 / 手机号 / 角色）"
              className="w-full h-9 pl-9 pr-8 rounded-lg border border-gray-200 text-sm bg-gray-50 focus:bg-white focus:outline-none focus:ring-1 focus:ring-primary-500"
            />
            {search && (
              <button onClick={() => setSearch('')} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                <X size={14} />
              </button>
            )}
          </div>

          {/* Invite button */}
          {isAdmin && !showInvite && (
            <button
              onClick={() => setShowInvite(true)}
              className="w-full flex items-center justify-center gap-2 h-10 text-sm font-medium text-primary-600 bg-primary-50 hover:bg-primary-100 rounded-lg transition-colors"
            >
              <Link2 size={16} />生成邀请链接
            </button>
          )}

          {/* Invite link generator */}
          {showInvite && (
            <div className="bg-gray-50 rounded-xl p-4 space-y-3 border border-gray-200">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-gray-700">生成邀请链接</span>
                <button onClick={handleResetInvite} className="text-gray-400 hover:text-gray-600">
                  <X size={14} />
                </button>
              </div>

              {!generatedLink ? (
                <>
                  {/* Role selector */}
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">角色</label>
                    <select
                      value={invRole} onChange={e => setInvRole(e.target.value)}
                      className="w-full h-9 px-3 rounded-lg border border-gray-200 text-sm bg-white focus:outline-none focus:ring-1 focus:ring-primary-500"
                    >
                      <option value="interviewer">面试官</option>
                      <option value="vip">VIP面试官</option>
                      <option value="hr">HR</option>
                      <option value="it">IT资源官</option>
                      <option value="referrer">内推官</option>
                    </select>
                  </div>
                  {/* Position checkboxes (hidden for referrer) */}
                  {invRole !== 'referrer' && <div>
                    <label className="block text-xs text-gray-500 mb-1">可查看的岗位</label>
                    <div className="flex flex-wrap gap-1.5">
                      {positions.length > 0 && (() => {
                        const allSelected = positions.every(p => invPositionIds.includes(p.id));
                        return (
                          <button
                            onClick={() => setInvPositionIds(allSelected ? [] : positions.map(p => p.id))}
                            className={clsx(
                              'px-2 py-1 rounded-md text-xs border font-medium transition-colors',
                              allSelected ? 'bg-primary-600 border-primary-600 text-white' : 'bg-primary-50 border-primary-300 text-primary-700'
                            )}
                          >
                            {allSelected ? '取消全选' : '全选岗位'}
                          </button>
                        );
                      })()}
                      {positions.map(p => (
                        <button key={p.id}
                          onClick={() => handlePositionToggle(p.id, invPositionIds, setInvPositionIds)}
                          className={clsx(
                            'px-2 py-1 rounded-md text-xs border transition-colors',
                            invPositionIds.includes(p.id) ? 'bg-primary-50 border-primary-300 text-primary-700' : 'bg-white border-gray-200 text-gray-500 hover:border-gray-300'
                          )}
                        >
                          {p.name}
                        </button>
                      ))}
                      {positions.length === 0 && <span className="text-xs text-gray-400">暂无岗位</span>}
                    </div>
                  </div>}
                  {invRole === 'referrer' && (
                    <p className="text-xs text-gray-400 bg-purple-50 rounded-lg px-3 py-2">内推官链接为公共链接，员工扫码后输入姓名和手机号即可自助注册为内推官，无需登录系统。</p>
                  )}
                  <button onClick={handleGenerateLink}
                    className="w-full h-9 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 rounded-lg transition-colors"
                  >
                    生成链接
                  </button>
                </>
              ) : (
                <div className="space-y-3">
                  <div className="bg-white rounded-lg border border-gray-200 p-3">
                    <p className="text-[11px] text-gray-400 mb-1.5">{invRole === 'referrer' ? '内推官链接（长期有效，可多人使用）' : '邀请链接（7天有效，仅限一人使用）'}</p>
                    <div className="flex items-center gap-2">
                      <input
                        readOnly
                        value={generatedLink}
                        className="flex-1 text-xs text-gray-700 bg-gray-50 rounded px-2 py-1.5 border border-gray-100 truncate"
                        onFocus={e => e.target.select()}
                      />
                      <button
                        onClick={handleCopyLink}
                        className={clsx(
                          'shrink-0 flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors',
                          linkCopied
                            ? 'bg-green-50 text-green-600'
                            : 'bg-primary-50 text-primary-600 hover:bg-primary-100'
                        )}
                      >
                        {linkCopied ? <><Check size={13} />已复制</> : <><Copy size={13} />复制</>}
                      </button>
                      {invRole === 'referrer' && (
                        <button
                          onClick={handleOpenQrCode}
                          className="shrink-0 flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-medium bg-purple-50 text-purple-600 hover:bg-purple-100 transition-colors"
                        >
                          <QrCode size={13} />二维码
                        </button>
                      )}
                    </div>
                  </div>
                  <button onClick={handleResetInvite}
                    className="w-full h-8 text-xs text-gray-500 hover:text-gray-700 transition-colors"
                  >
                    再生成一个
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Users list grouped by role */}
          {loading ? (
            <div className="text-center py-8 text-gray-400 text-sm">加载中...</div>
          ) : filteredUsers.length === 0 ? (
            <div className="text-center py-8 text-gray-400 text-sm">未找到匹配的成员</div>
          ) : (
            <div className="space-y-4">
              {visibleRoles.map(role => (
                <div key={role}>
                  <button
                    onClick={() => toggleRoleGroup(role)}
                    className="w-full flex items-center gap-2 mb-2 text-left"
                  >
                    {collapsedRoles[role] ? <ChevronRight size={15} className="text-gray-400" /> : <ChevronDown size={15} className="text-gray-400" />}
                    <span className={clsx('px-2 py-0.5 rounded text-[11px] font-medium', ROLE_COLORS[role])}>{ROLE_LABELS[role]}</span>
                    <span className="text-[11px] text-gray-400">{(groupedUsers[role] || []).length} 人</span>
                  </button>
                  {!collapsedRoles[role] && (
                  <div className="space-y-2">
              {(groupedUsers[role] || []).map(u => (
                <div key={u.id} className="bg-white border border-gray-200 rounded-xl p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 rounded-full bg-gray-100 flex items-center justify-center text-sm font-bold text-gray-500">
                        {u.display_name?.charAt(0) || u.username.charAt(0)}
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-medium text-gray-900">{u.display_name || u.username}</span>
                          <span className={clsx('px-1.5 py-0.5 rounded text-[10px] font-medium', ROLE_COLORS[u.role])}>
                            {ROLE_LABELS[u.role]}
                          </span>
                          {u.status === 'disabled' && (
                            <span className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-red-50 text-red-500">已禁用</span>
                          )}
                          {u.status === 'pending' && (
                            <span className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-amber-50 text-amber-600 border border-amber-200">待审批</span>
                          )}
                          {u.id === currentUser.id && (
                            <span className="text-[10px] text-gray-400">(我)</span>
                          )}
                        </div>
                        <span className="text-[11px] text-gray-400">@{u.username}</span>
                        {u.phone ? (
                          <span className="text-[11px] text-gray-400">📱 {u.phone}</span>
                        ) : isAdmin ? (
                          <button onClick={async () => {
                            const ph = prompt('设置手机号（用于内推海报验证）');
                            if (ph) { await updateUser(u.id, { phone: ph }); fetchUsers(); toast.success('手机号已设置'); }
                          }} className="text-[11px] text-indigo-500 hover:text-indigo-700">+ 设置手机号</button>
                        ) : null}
                      </div>
                    </div>

                    {isAdmin && u.id !== currentUser.id && u.role !== 'admin' && (
                      <div className="flex items-center gap-1">
                        {isAdmin && (
                          <select
                            value={u.role}
                            onChange={e => handleRoleChange(u.id, e.target.value)}
                            className="h-7 px-2 rounded border border-gray-200 text-[11px] bg-gray-50"
                          >
                            <option value="interviewer">面试官</option>
                            <option value="vip">VIP面试官</option>
                            <option value="hr">HR</option>
                            <option value="it">IT资源官</option>
                            <option value="referrer">内推官</option>
                          </select>
                        )}
                        <button
                          onClick={() => handleStatusToggle(u.id, u.status)}
                          className={clsx(
                            'px-2 py-1 rounded text-[11px] transition-colors',
                            u.status === 'active' ? 'text-yellow-600 hover:bg-yellow-50' : 'text-green-600 hover:bg-green-50'
                          )}
                        >
                          {u.status === 'active' ? '禁用' : u.status === 'pending' ? '激活' : '启用'}
                        </button>
                        <button
                          onClick={() => handleDelete(u.id, u.display_name || u.username)}
                          className="p-1 text-gray-400 hover:text-red-500 rounded"
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    )}
                  </div>

                  {/* Password reset */}
                  {isAdmin && u.id !== currentUser.id && u.role !== 'admin' && u.role !== 'referrer' && (
                    <div className="mt-3 pt-3 border-t border-gray-100">
                      <div className="flex items-center justify-between mb-1.5">
                        <span className="text-[11px] text-gray-500">密码管理</span>
                        {resettingPassword === u.id ? (
                          <button onClick={() => { setResettingPassword(null); setNewPassword(''); }}
                            className="text-[11px] text-gray-400 hover:text-gray-600">取消</button>
                        ) : (
                          <button onClick={() => { setResettingPassword(u.id); setNewPassword(''); }}
                            className="text-[11px] text-orange-600 hover:underline">重置密码</button>
                        )}
                      </div>
                      {resettingPassword === u.id && (
                        <div className="flex items-center gap-2">
                          <input
                            type="text"
                            value={newPassword}
                            onChange={e => setNewPassword(e.target.value)}
                            placeholder="新密码（至少4位）"
                            className="flex-1 h-7 px-2 rounded border border-gray-200 text-[11px] bg-white focus:outline-none focus:ring-1 focus:ring-primary-500"
                            onKeyDown={e => e.key === 'Enter' && handleResetPassword(u.id)}
                          />
                          <button onClick={() => handleResetPassword(u.id)}
                            className="px-2.5 h-7 text-[11px] font-medium text-white bg-orange-500 hover:bg-orange-600 rounded transition-colors"
                          >确认</button>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Position permissions */}
                  {isAdmin && u.role !== 'admin' && u.role !== 'referrer' && (
                    <div className="mt-3 pt-3 border-t border-gray-100">
                      <div className="flex items-center justify-between mb-1.5">
                        <span className="text-[11px] text-gray-500 flex items-center gap-1">
                          <Eye size={11} />可查看岗位
                        </span>
                        {editingPositions !== u.id ? (
                          <button
                            onClick={() => setEditingPositions(u.id)}
                            className="text-[11px] text-primary-600 hover:underline"
                          >编辑</button>
                        ) : (
                          <button
                            onClick={() => handleSavePositions(u.id, u.position_ids || [])}
                            className="text-[11px] text-primary-600 font-medium hover:underline"
                          >保存</button>
                        )}
                      </div>
                      {editingPositions === u.id ? (
                        <div className="flex flex-wrap gap-1.5">
                          {positions.length > 0 && (() => {
                            const allSelected = positions.every(p => (u.position_ids || []).includes(p.id));
                            return (
                              <button
                                onClick={() => {
                                  const next = allSelected ? [] : positions.map(p => p.id);
                                  setUsers(prev => prev.map(x => x.id === u.id ? { ...x, position_ids: next } : x));
                                }}
                                className={clsx(
                                  'px-2 py-0.5 rounded text-[11px] border font-medium transition-colors',
                                  allSelected ? 'bg-primary-600 border-primary-600 text-white' : 'bg-primary-50 border-primary-300 text-primary-700'
                                )}
                              >
                                {allSelected ? '取消全选' : '全选岗位'}
                              </button>
                            );
                          })()}
                          {positions.map(p => (
                            <button key={p.id}
                              onClick={() => {
                                const cur = u.position_ids || [];
                                const next = cur.includes(p.id) ? cur.filter(id => id !== p.id) : [...cur, p.id];
                                setUsers(prev => prev.map(x => x.id === u.id ? { ...x, position_ids: next } : x));
                              }}
                              className={clsx(
                                'px-2 py-0.5 rounded text-[11px] border transition-colors',
                                (u.position_ids || []).includes(p.id) ? 'bg-primary-50 border-primary-300 text-primary-700' : 'bg-white border-gray-200 text-gray-400'
                              )}
                            >
                              {p.name}
                            </button>
                          ))}
                        </div>
                      ) : (
                        <div className="flex flex-wrap gap-1">
                          {(u.position_ids || []).length > 0 ? (
                            positions.filter(p => (u.position_ids || []).includes(p.id)).map(p => (
                              <span key={p.id} className="px-2 py-0.5 rounded bg-gray-50 text-[11px] text-gray-600">{p.name}</span>
                            ))
                          ) : (
                            <span className="text-[11px] text-gray-300">未分配岗位</span>
                          )}
                        </div>
                      )}
                    </div>
                  )}

                  {/* Feishu binding */}
                  {isAdmin && (
                    <div className="mt-3 pt-3 border-t border-gray-100">
                      <div className="flex items-center justify-between mb-1.5">
                        <span className="text-[11px] text-gray-500 flex items-center gap-1">
                          飞书绑定
                        </span>
                        <div className="flex items-center gap-2">
                          {u.feishu_open_id ? (
                            <>
                              <span className="text-[10px] text-green-600 bg-green-50 px-1.5 py-0.5 rounded">已绑定</span>
                              {bindingFeishu === u.id ? (
                                <button
                                  onClick={() => { setBindingFeishu(null); setFeishuContact(''); }}
                                  className="text-[11px] text-gray-400 hover:text-gray-600"
                                >取消</button>
                              ) : (
                                <>
                                  <button
                                    onClick={() => { setBindingFeishu(u.id); setFeishuContact(''); }}
                                    className="text-[11px] text-primary-600 hover:underline"
                                  >换绑</button>
                                  <button
                                    onClick={() => handleUnbindFeishu(u.id)}
                                    className="text-[11px] text-red-500 hover:underline"
                                  >解绑</button>
                                </>
                              )}
                            </>
                          ) : bindingFeishu === u.id ? (
                            <button
                              onClick={() => { setBindingFeishu(null); setFeishuContact(''); }}
                              className="text-[11px] text-gray-400 hover:text-gray-600"
                            >取消</button>
                          ) : (
                            <button
                              onClick={() => { setBindingFeishu(u.id); setFeishuContact(''); }}
                              className="text-[11px] text-primary-600 hover:underline"
                            >绑定</button>
                          )}
                        </div>
                      </div>
                      {bindingFeishu === u.id && (
                        <div className="flex items-center gap-2">
                          <input
                            value={feishuContact}
                            onChange={e => setFeishuContact(e.target.value)}
                            placeholder="飞书手机号或邮箱"
                            className="flex-1 h-7 px-2 rounded border border-gray-200 text-[11px] bg-white focus:outline-none focus:ring-1 focus:ring-primary-500"
                            onKeyDown={e => e.key === 'Enter' && handleBindFeishu(u.id)}
                          />
                          <button
                            onClick={() => handleBindFeishu(u.id)}
                            className="px-2.5 h-7 text-[11px] font-medium text-white bg-primary-600 hover:bg-primary-700 rounded transition-colors"
                          >确认</button>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Per-user permission override */}
                  {isAdmin && u.role !== 'admin' && (
                    <div className="mt-3 pt-3 border-t border-gray-100">
                      <div className="flex items-center justify-between mb-1.5">
                        <span className="text-[11px] text-gray-500 flex items-center gap-1">
                          <Shield size={11} />功能权限
                        </span>
                        <button onClick={() => openPermsEditor(u.id)} className="text-[11px] text-primary-600 hover:underline">
                          {editingPerms === u.id ? '收起' : '设置'}
                        </button>
                      </div>
                      {editingPerms === u.id && permDraft && (
                        <div className="space-y-2">
                          <div className="text-[10px] text-gray-400">岗位查看范围</div>
                          <div className="flex gap-1.5">
                            {(['all', 'specific'] as const).map(s => (
                              <button key={s}
                                onClick={() => setPermDraft(p => p ? { ...p, position_scope: s } : p)}
                                className={clsx('px-2.5 py-1 rounded-md text-[11px] border transition-colors',
                                  permDraft.position_scope === s ? 'bg-primary-50 border-primary-300 text-primary-700' : 'bg-white border-gray-200 text-gray-500')}
                              >{s === 'all' ? '全部岗位' : '指定岗位'}</button>
                            ))}
                          </div>
                          <div className="text-[10px] text-gray-400">可见功能模块</div>
                          <div className="flex flex-wrap gap-1.5">
                            {moduleDefs.map(m => (
                              <button key={m.key}
                                onClick={() => togglePermModule(m.key)}
                                className={clsx('px-2 py-0.5 rounded text-[11px] border transition-colors',
                                  permDraft.modules.includes(m.key) ? 'bg-primary-50 border-primary-300 text-primary-700' : 'bg-white border-gray-200 text-gray-400')}
                              >{m.label}</button>
                            ))}
                          </div>
                          <div className="flex items-center gap-2 pt-1">
                            <button onClick={() => handleSavePerms(u.id)} disabled={savingPerms}
                              className="px-2.5 h-7 text-[11px] font-medium text-white bg-primary-600 hover:bg-primary-700 rounded disabled:opacity-40">保存</button>
                            <button onClick={() => handleResetPerms(u.id)} disabled={savingPerms}
                              className="flex items-center gap-1 px-2.5 h-7 text-[11px] text-gray-500 hover:text-gray-700"><RotateCcw size={11} />恢复默认</button>
                            <span className="text-[10px] text-gray-300 ml-auto">{permDraft.usesOverride ? '已自定义' : '继承角色默认'}</span>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
                  </div>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* Backup / Export section (admin) */}
          {isAdmin && (
            <div className="bg-gray-50 rounded-xl border border-gray-200 p-4 space-y-3">
              <div className="flex items-center gap-2">
                <Database size={15} className="text-gray-500" />
                <span className="text-sm font-medium text-gray-700">数据备份与导出</span>
              </div>
              <p className="text-[11px] text-gray-400">
                导出独立备份包（ZIP），包含候选人、面试评价、薪酬材料、Offer方案、筛选记录、面试记录、活动日志等全量数据及所有上传文件。备份为独立SQLite数据库，可用于数据迁移或灾难恢复。
              </p>

              {!backupStats ? (
                <button
                  onClick={fetchBackupStats}
                  disabled={loadingBackup}
                  className="w-full h-9 text-sm font-medium text-primary-600 bg-primary-50 hover:bg-primary-100 rounded-lg transition-colors disabled:opacity-40"
                >
                  {loadingBackup ? '加载中...' : '查看备份概览'}
                </button>
              ) : (
                <div className="space-y-2">
                  <div className="grid grid-cols-3 gap-2">
                    {[
                      { label: '候选人', val: backupStats.candidates },
                      { label: '面试评价', val: backupStats.evaluations },
                      { label: '薪酬材料', val: backupStats.salary_materials },
                      { label: 'Offer审批', val: backupStats.offer_approvals },
                      { label: '面试记录', val: backupStats.interview_records },
                      { label: '筛选记录', val: backupStats.screening_records },
                    ].map(item => (
                      <div key={item.label} className="bg-white rounded-lg border border-gray-100 px-2.5 py-2 text-center">
                        <div className="text-lg font-bold text-gray-800">{item.val}</div>
                        <div className="text-[10px] text-gray-400">{item.label}</div>
                      </div>
                    ))}
                  </div>
                  <div className="flex items-center justify-between text-[11px] text-gray-400 px-1">
                    <span>上传文件: {backupStats.uploaded_files} 个</span>
                    <span>文件大小: {backupStats.total_file_size_mb} MB</span>
                  </div>
                  <button
                    onClick={handleDownloadBackup}
                    disabled={downloadingBackup}
                    className="w-full h-9 flex items-center justify-center gap-1.5 text-sm font-medium text-white bg-green-600 hover:bg-green-700 rounded-lg transition-colors disabled:opacity-40"
                  >
                    <Download size={15} />
                    {downloadingBackup ? '正在打包...' : '下载备份包 (ZIP)'}
                  </button>
                </div>
              )}
            </div>
          )}
          </>)}
        </div>
      </div>
  );

  if (embedded) return <div className="h-full flex flex-col bg-gray-50">{content}</div>;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />
      {content}
    </div>
  );
}
